import { Building2, CreditCard, KeyRound, LogIn, Mail, ReceiptIndianRupee, Shapes, ShieldCheck, Users, UserPlus } from "lucide-react";
import { Suspense, lazy, type ComponentType } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { defineFeature } from "../types";

/**
 * Settings — the /settings shell plus the workspace (admin) half of it.
 *
 * Registering the `settings/*` route is what removes the stand-in the router
 * mounts while nothing serves it. The shell itself renders whatever
 * `settingsSections` the registry reports, so the personal sections contributed
 * by the `account` feature appear here with no coupling between the two.
 *
 * Every page is `React.lazy`, so this module costs the entry bundle nothing but
 * its icons and section metadata.
 */

const OrganizationPage = lazy(() => import("@/app/settings/workspace/organization"));
const MembersPage = lazy(() => import("@/app/settings/workspace/members"));
const RolesPage = lazy(() => import("@/app/settings/workspace/roles"));
const ApiKeysPage = lazy(() => import("@/app/settings/workspace/api-keys"));
const CustomFieldsPage = lazy(() => import("@/app/settings/workspace/custom-fields"));
const SignInPage = lazy(() => import("@/app/settings/workspace/sign-in"));
const OutgoingMailPage = lazy(() => import("@/app/settings/workspace/outgoing-mail"));
const PlanBillingPage = lazy(() => import("@/app/settings/workspace/billing"));

function SectionFallback() {
  return (
    <div className="grid w-full gap-5" role="status" aria-label="Loading settings">
      <div className="grid gap-2">
        <Skeleton className="h-6 w-56" />
        <Skeleton className="h-4 w-96" />
      </div>
      <Skeleton className="h-52 w-full" rounded="card" />
      <Skeleton className="h-72 w-full" rounded="card" />
    </div>
  );
}

function section(Page: ComponentType) {
  return (
    <Suspense fallback={<SectionFallback />}>
      <Page />
    </Suspense>
  );
}

export default defineFeature({
  key: "settings",
  title: "Settings",
  entities: ["org", "member", "invitation", "role", "api_key", "custom_field"],
  routes: [{ path: "settings/*", lazy: () => import("@/app/settings/layout") }],
  settingsSections: [
    {
      key: "organization",
      label: "Organization",
      path: "organization",
      group: "Workspace",
      order: 100,
      icon: Building2,
      description: "Profile, branding, GSTIN, currency and invoice numbering",
      element: section(OrganizationPage),
    },
    {
      // Lean2Automate's licence to this workspace, for whoever pays for it
      // (billing:manage) or runs it (org:manage). The server guards the same.
      key: "billing",
      label: "Plan & billing",
      path: "billing",
      group: "Workspace",
      order: 105,
      icon: CreditCard,
      permission: ["billing:manage", "org:manage"],
      permissionMode: "any",
      description: "Your licence and its end date, staff seats, and Lean2Automate's invoices",
      element: section(PlanBillingPage),
    },
    {
      key: "members",
      label: "Members",
      path: "members",
      group: "Workspace",
      order: 110,
      icon: Users,
      description: "Your team, their roles, rates and capacity",
      element: section(MembersPage),
    },
    {
      key: "roles",
      label: "Roles & permissions",
      path: "roles",
      group: "Workspace",
      order: 120,
      icon: ShieldCheck,
      permission: "roles:manage",
      description: "What each role in the workspace is allowed to do",
      element: section(RolesPage),
    },
    {
      key: "api-keys",
      label: "API keys",
      path: "api-keys",
      group: "Workspace",
      order: 130,
      icon: KeyRound,
      permission: "integrations:manage",
      description: "Credentials for the public API and your own scripts",
      element: section(ApiKeysPage),
    },
    {
      key: "sign-in",
      label: "Sign-in & SSO",
      path: "sign-in",
      group: "Workspace",
      order: 125,
      icon: LogIn,
      permission: "org:manage",
      description: "Passwords, your own identity provider, or either",
      element: section(SignInPage),
    },
    {
      key: "outgoing-mail",
      label: "Outgoing mail",
      path: "outgoing-mail",
      group: "Workspace",
      order: 135,
      icon: Mail,
      permission: "org:manage",
      description: "Send invoices and portal invitations from your own domain",
      element: section(OutgoingMailPage),
    },
    {
      key: "custom-fields",
      label: "Custom fields",
      path: "custom-fields",
      group: "Workspace",
      order: 140,
      icon: Shapes,
      description: "Extra fields on leads, clients, projects and invoices",
      element: section(CustomFieldsPage),
    },
  ],
  quickCreate: [
    {
      key: "settings.invite",
      label: "Teammate",
      icon: UserPlus,
      to: "/settings/members?invite=1",
      permission: "members:manage",
      order: 90,
      description: "Invite someone to this workspace",
    },
  ],
  commands: [
    {
      key: "settings.organization",
      label: "Workspace settings",
      icon: Building2,
      to: "/settings/organization",
      group: "navigate",
      keywords: ["org", "company", "branding", "logo", "address", "currency", "timezone"],
      description: "Profile, branding and locale",
    },
    {
      key: "settings.invoicing",
      label: "GSTIN & invoice numbering",
      icon: ReceiptIndianRupee,
      to: "/settings/organization",
      group: "actions",
      keywords: ["gst", "gstin", "pan", "tax", "prefix", "lut", "bank", "upi", "sac"],
      description: "Tax registration, numbering and payment details",
    },
    {
      key: "settings.billing",
      label: "Plan & billing",
      icon: CreditCard,
      to: "/settings/billing",
      permission: ["billing:manage", "org:manage"],
      permissionMode: "any",
      group: "navigate",
      keywords: ["licence", "license", "subscription", "plan", "renew", "seats", "lean2automate invoice", "export"],
      description: "Licence, seats and Lean2Automate's invoices",
    },
    {
      key: "settings.members",
      label: "Members",
      icon: Users,
      to: "/settings/members",
      group: "navigate",
      keywords: ["team", "people", "staff", "seats", "users"],
    },
    {
      key: "settings.invite",
      label: "Invite a teammate",
      icon: UserPlus,
      to: "/settings/members?invite=1",
      permission: "members:manage",
      group: "actions",
      keywords: ["add user", "new member", "invitation"],
    },
    {
      key: "settings.roles",
      label: "Roles & permissions",
      icon: ShieldCheck,
      to: "/settings/roles",
      permission: "roles:manage",
      group: "navigate",
      keywords: ["access", "rights", "admin", "permission matrix"],
    },
    {
      key: "settings.api-keys",
      label: "API keys",
      icon: KeyRound,
      to: "/settings/api-keys",
      permission: "integrations:manage",
      group: "navigate",
      keywords: ["token", "secret", "integration", "webhook", "zapier"],
    },
    {
      key: "settings.custom-fields",
      label: "Custom fields",
      icon: Shapes,
      to: "/settings/custom-fields",
      group: "navigate",
      keywords: ["data model", "attributes", "properties", "cf"],
    },
  ],
});
